import { CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReactNode, useEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";

export interface Option {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  disabledText?: string;
}

interface SelectPopoverProps extends Omit<SelectDynamicPopoverProps, 'children'> {
  trigger: ReactNode;
  title?: string;
  options: Option[];
  value: string;
  itemClassName?: string;
  /** 列表区域容器 class，默认带 p-1.5 */
  optionsContainerClassName?: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
}

export const SelectPopover = ({
  trigger,
  title,
  options,
  value,
  className,
  side = 'top',
  align = 'center',
  itemClassName,
  optionsContainerClassName,
  onValueChange,
  disabled = false,
  ...rest
}: SelectPopoverProps) => {

  return (
    <SelectDynamicPopover
      trigger={trigger}
      className={className}
      side={side}
      align={align}
      disabled={disabled}
      // triggerHidden={false}
      {...rest}
    >
      <div
        className="flex flex-col max-h-[370px]"
      >
        {title && (
          <p className="text-xs text-[rgba(152,162,179,1)] pt-[16px] px-[14px] flex-shrink-0">
            {title}
          </p>
        )}
        <div
          className={cn(
            'space-y-1 overflow-y-auto p-1.5 scrollbar-hide mt-auto',
            optionsContainerClassName,
          )}
        >
          {options.map((item) => {
            const content = (
              <div
                className={cn(
                  "relative flex items-center gap-2 py-[9px] px-[10px] hover:bg-[rgba(16,24,40,0.05)] rounded-[8px] cursor-pointer",
                  itemClassName,
                  value === item.value && "bg-[rgba(16,24,40,0.05)]",
                  item.disabled && "cursor-not-allowed opacity-50",
                )}
                onClick={() => {
                  if (item.disabled) return;
                  onValueChange(item.value);
                }}
              >
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span className="text-sm text-[rgba(16,24,40,1)] flex-grow truncate">
                  {item.label}
                </span>
                {value === item.value && (
                  <CheckIcon className="size-4 text-[rgba(16,24,40,1)] flex-shrink-0" />
                )}
              </div>
            );

            // 针对 disabled 项，使用受控 Popover 模拟 Tooltip (Hover 触发)
            if (item.disabled) {
              return (
                <DisabledItemTooltip
                  key={item.value}
                  content={content}
                  text={item.disabledText}
                />
              );
            }

            return <div key={item.value}>{content}</div>;
          })}
        </div>
      </div>
    </SelectDynamicPopover>
  )
};

// 禁用提示组件
const DisabledItemTooltip = ({ content, text }: { content: React.ReactNode, text?: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className="w-full">{content}</div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={5}
          className={cn(
            "z-[999] pointer-events-none py-1.5 px-3 rounded-[6px] bg-[rgba(16,24,40,0.9)]",
            "text-white text-xs shadow-xl outline-none",
            "animate-in fade-in zoom-in-95 duration-200"
          )}
        >
          {text || 'Disabled'}
          {/* <Popover.Arrow className="fill-[rgba(16,24,40,0.9)]" /> */}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// 暂时单独抽出 SelectDynamicPopover 组件
// 用于 SelectPopover 组件
// 解决 triggerHidden && handleOpenChange(false) 这一行问题
export interface SelectDynamicPopoverProps {
  className?: string;
  trigger: React.ReactNode;
  triggerClassName?: string;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "end" | "center";
  sideOffset?: number;
  alignOffset?: number;
  mode?: "click" | "hover";
  triggerHidden?: boolean;
  delay?: number;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 在触发器右侧放置 Popover.Anchor，使浮层相对「面板右缘」定位（用于嵌套在输出设置卡片内时语言列表出现在整张卡片右侧）
   */
  anchorToTriggerEnd?: boolean;
  avoidCollisions?: boolean;
}

export function SelectDynamicPopover({
  className,
  triggerClassName,
  trigger,
  children,
  side = "top",
  align = "start",
  sideOffset = 8,
  alignOffset = 0,
  mode = "click",
  triggerHidden = true,
  delay = 200,
  disabled = false,
  onOpenChange,
  anchorToTriggerEnd = false,
  avoidCollisions = true,
}: SelectDynamicPopoverProps) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setHidden(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleHide = () => {
    if (mode === "hover") {
      clearHideTimeout();
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, delay);
    }
  };

  const handleTriggerMouseEnter = () => {
    if (disabled) return; // 禁用时不响应
    if (mode === "hover") {
      clearHideTimeout();
      setOpen(true);
    }
  };

  const handleTriggerMouseLeave = () => {
    if (mode === "hover") {
      scheduleHide();
    }
  };

  const handleContentMouseEnter = () => {
    if (mode === "hover") {
      clearHideTimeout();
    }
  };

  const handleContentMouseLeave = () => {
    if (mode === "hover") {
      scheduleHide();
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled && nextOpen) return; // 禁用时阻止打开
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const triggerProps =
    mode === "hover"
      ? {
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleTriggerMouseLeave,
      }
      : {};

  const contentProps =
    mode === "hover"
      ? {
        onMouseEnter: handleContentMouseEnter,
        onMouseLeave: handleContentMouseLeave,
      }
      : {};

  return (
    <Popover.Root open={disabled ? false : open} onOpenChange={handleOpenChange}>
      <div
        {...triggerProps}
        className={cn(
          anchorToTriggerEnd ? "relative w-full min-w-0" : "w-fit",
          disabled && "!cursor-not-allowed opacity-50", // 禁用样式
          triggerClassName
        )}
      >
        <Popover.Trigger
          asChild
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            if (hidden) {
              e.preventDefault();
              e.stopPropagation();
              setHidden(false);
            }
          }}
        >
          {trigger}
        </Popover.Trigger>
        {anchorToTriggerEnd && (
          <Popover.Anchor asChild>
            <div
              className="pointer-events-none absolute left-full top-0 h-full w-px shrink-0"
              aria-hidden
            />
          </Popover.Anchor>
        )}
      </div>

      {!disabled && (
        <Popover.Portal>
          <Popover.Content
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            side={side}
            align={align}
            avoidCollisions={avoidCollisions}
            onOpenAutoFocus={(e) => e.preventDefault()}
            {...contentProps}
            className={cn(
              "z-50 w-56 rounded-sm border bg-popover p-1 text-popover-foreground shadow-md outline-none",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2",
              "data-[side=top]:slide-in-from-bottom-2",
              hidden && "invisible",
              className
            )}
            onClickCapture={() => triggerHidden && handleOpenChange(false)}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      )}
    </Popover.Root>
  );
}
